import type { DocumentProfile, ValidationReport } from '@photovalidator/core';
import { createCanvas } from '../image/bitmap.js';
import type { PhotoStage } from '../stage/photoStage.js';
import type { ValidatorClient } from '../validator-client/types.js';
import { el } from '../ui/dom.js';

/**
 * Live camera mode.
 *
 * The live preview reuses the *same* stage and the *same* validator-core: each
 * throttled preview frame is analysed through the normal pipeline and its report
 * drives the very same guides the uploaded photo uses. Heavy analysis never runs
 * at frame rate — the video is drawn at ~30 fps, but analysis runs 2–3 times per
 * second on a downscaled frame.
 */

export interface CameraHint {
  id: string;
  label: string;
  status: 'pass' | 'warning' | 'fail' | 'uncertain';
}

export interface CameraControllerOptions {
  profile: DocumentProfile;
  stage: PhotoStage;
  client: ValidatorClient;
  onCapture(blob: Blob): void;
  onError(message: string): void;
  /** Preview analysis interval in milliseconds. */
  previewIntervalMs?: number;
  /** Long side of the frame used for preview analysis. */
  previewMaxSide?: number;
}

export class CameraController {
  readonly element: HTMLElement;

  private readonly video: HTMLVideoElement;
  private readonly hintsLayer: HTMLElement;
  private readonly captureButton: HTMLButtonElement;
  private readonly stopButton: HTMLButtonElement;
  private stream: MediaStream | null = null;
  private rafHandle: number | null = null;
  private previewTimer: number | null = null;
  private analysing = false;
  private running = false;

  constructor(private readonly options: CameraControllerOptions) {
    /**
     * The camera frames are painted into the *stage* canvas, which is the only
     * thing the user sees; this video element is a decoding source and stays
     * detached from the DOM (a hidden element in the tree was dead markup).
     */
    this.video = el('video', {
      class: 'pv-camera__video',
      playsinline: true,
      autoplay: true,
      muted: true,
    }) as HTMLVideoElement;
    this.hintsLayer = el('div', { class: 'pv-camera__hints' });
    this.captureButton = el('button', {
      class: 'pv-button pv-button--primary',
      type: 'button',
      text: 'Сделать снимок',
    }) as HTMLButtonElement;
    this.stopButton = el('button', { class: 'pv-button', type: 'button', text: 'Остановить камеру' }) as HTMLButtonElement;
    this.captureButton.addEventListener('click', () => void this.capture());
    this.stopButton.addEventListener('click', () => this.stop());

    this.element = el('section', { class: 'pv-panel' }, [
      el('h2', { class: 'pv-panel__title', text: 'Съёмка на камеру' }),
      el('div', { class: 'pv-camera' }, [
        el('p', {
          class: 'pv-small pv-muted',
          'data-role': 'camera-caption',
          text: 'Превью камеры отображается в области фото выше. Держите лицо в зелёной зоне — подсказки появляются под этим текстом.',
        }),
        this.hintsLayer,
      ]),
      el('div', { class: 'pv-camera__controls' }, [this.captureButton, this.stopButton]),
      el('p', {
        class: 'pv-small pv-muted',
        text: 'Направляющие на экране — те же, что и при проверке загруженного фото: зелёная зона головы, линия глаз и центральная линия. Кадр анализируется на вашем устройстве.',
      }),
    ]);
  }

  get isRunning(): boolean {
    return this.running;
  }

  async start(): Promise<void> {
    if (this.running) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.options.onError('Камера недоступна в этом браузере.');
      return;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
    } catch (error) {
      this.options.onError(
        `Не удалось получить доступ к камере: ${error instanceof Error ? error.message : String(error)}`,
      );
      return;
    }

    this.video.srcObject = this.stream;
    await this.video.play().catch(() => undefined);
    this.running = true;
    this.options.stage.applyState({ mode: 'live' });
    this.loop();
    this.previewTimer = setInterval(() => void this.analysePreview(), this.options.previewIntervalMs ?? 450) as unknown as number;
    void this.analysePreview();
  }

  stop(): void {
    this.running = false;
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    if (this.previewTimer !== null) clearInterval(this.previewTimer);
    this.rafHandle = null;
    this.previewTimer = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    this.video.srcObject = null;
    this.options.stage.applyState({ mode: 'report' });
    this.hintsLayer.replaceChildren();
  }

  async capture(): Promise<void> {
    const width = this.video.videoWidth;
    const height = this.video.videoHeight;
    if (width === 0 || height === 0) {
      this.options.onError('Кадр ещё не готов — попробуйте через секунду.');
      return;
    }
    const { canvas, ctx } = createCanvas(width, height);
    ctx.drawImage(this.video, 0, 0, width, height);
    const blob = await blobFromCanvas(canvas);
    this.stop();
    this.options.onCapture(blob);
  }

  dispose(): void {
    this.stop();
  }

  private loop = (): void => {
    if (!this.running) return;
    if (this.video.readyState >= 2) {
      this.options.stage.drawSource(this.video, this.video.videoWidth, this.video.videoHeight);
    }
    this.rafHandle = requestAnimationFrame(this.loop);
  };

  private async analysePreview(): Promise<void> {
    if (!this.running || this.analysing || this.video.readyState < 2) return;
    this.analysing = true;
    try {
      const width = this.video.videoWidth;
      const height = this.video.videoHeight;
      if (width === 0 || height === 0) return;
      const scale = Math.min(1, (this.options.previewMaxSide ?? 640) / Math.max(width, height));
      const bitmap = await createImageBitmap(this.video, {
        resizeWidth: Math.round(width * scale),
        resizeHeight: Math.round(height * scale),
        resizeQuality: 'low',
      });
      const report = await this.options.client.preview(bitmap, this.options.profile);
      bitmap.close();
      if (!this.running) return;
      this.options.stage.applyState({ report });
      this.renderHints(hintsFromReport(report));
    } catch {
      // A dropped preview frame must never break the camera session.
    } finally {
      this.analysing = false;
    }
  }

  private renderHints(hints: CameraHint[]): void {
    this.hintsLayer.replaceChildren(
      ...hints.map((hint) =>
        el('span', { class: `pv-hint pv-hint--${hint.status}`, text: hint.label }),
      ),
    );
  }
}

const blobFromCanvas = async (canvas: OffscreenCanvas | HTMLCanvasElement): Promise<Blob> => {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.95 });
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Не удалось сохранить кадр.'))),
      'image/jpeg',
      0.95,
    );
  });
};

/**
 * Turns a fast validation report into the short hints shown over the preview.
 * The wording is deliberately operational: "отойдите дальше", not "FAIL".
 */
export const hintsFromReport = (report: ValidationReport): CameraHint[] => {
  const byId = new Map(report.checks.map((check) => [check.id, check]));
  const hints: CameraHint[] = [];

  const faceCount = byId.get('face_count');
  if (faceCount && faceCount.status === 'fail') {
    hints.push({ id: 'face', label: 'Лицо не найдено — встаньте в кадр', status: 'fail' });
    return hints;
  }
  if (faceCount && faceCount.status === 'not_checked') {
    hints.push({ id: 'face', label: 'Модель анализа лица недоступна', status: 'uncertain' });
    return hints;
  }
  if (faceCount) hints.push({ id: 'face', label: 'Лицо найдено', status: 'pass' });

  const head = byId.get('head_size');
  if (head) {
    if (head.status === 'fail') {
      const tooLarge = head.message.includes('большую');
      hints.push({
        id: 'head',
        label: tooLarge ? 'Слишком близко — отойдите дальше' : 'Слишком далеко — подойдите ближе',
        status: 'fail',
      });
    } else if (head.status === 'pass') {
      hints.push({ id: 'head', label: 'Размер головы в норме', status: 'pass' });
    } else if (head.status === 'warning') {
      hints.push({ id: 'head', label: 'Кадрируйте плотнее', status: 'warning' });
    }
  }

  const eyeLine = byId.get('eye_line');
  if (eyeLine && eyeLine.status === 'fail') {
    hints.push({
      id: 'eye',
      label: eyeLine.message.includes('высоко') ? 'Опустите камеру' : 'Поднимите камеру',
      status: 'fail',
    });
  } else if (eyeLine && eyeLine.status === 'pass') {
    hints.push({ id: 'eye', label: 'Глаза в зоне', status: 'pass' });
  }

  const pose = byId.get('face_pose');
  if (pose && pose.status === 'fail') {
    hints.push({ id: 'pose', label: 'Держите голову ровно, смотрите прямо', status: 'fail' });
  } else if (pose && (pose.status === 'warning' || pose.status === 'uncertain')) {
    hints.push({ id: 'pose', label: 'Поверните голову прямо', status: 'warning' });
  }

  const position = byId.get('face_position');
  if (position && position.status === 'fail') {
    hints.push({ id: 'center', label: 'Сместите лицо к центру кадра', status: 'warning' });
  }

  /**
   * Basic lighting hint: the preview frame is analysed at 240 px, which is
   * enough to notice "too dark" / "too bright" without a full-resolution pass.
   */
  const exposure = byId.get('exposure');
  if (exposure && (exposure.status === 'fail' || exposure.status === 'warning')) {
    const mean = exposure.value ?? 128;
    hints.push({
      id: 'light',
      label: mean < 100 ? 'Темно — найдите больше света' : 'Слишком ярко — уберите прямой свет',
      status: exposure.status === 'fail' ? 'fail' : 'warning',
    });
  }

  const background = byId.get('background');
  if (background && background.status === 'fail') {
    hints.push({ id: 'background', label: 'Фон: нужен ровный светлый фон', status: 'warning' });
  }

  const blocking = hints.filter((hint) => hint.status !== 'pass');
  if (blocking.length === 0) {
    hints.push({ id: 'ready', label: '✓ Всё готово — можно снимать', status: 'pass' });
  }

  // Never more than four at a time: the preview must stay readable.
  const ordered = [
    ...hints.filter((hint) => hint.id === 'face'),
    ...hints.filter((hint) => hint.id === 'head'),
    ...hints.filter((hint) => hint.id === 'eye'),
    ...hints.filter((hint) => hint.id === 'pose'),
    ...hints.filter((hint) => hint.id === 'center'),
    ...hints.filter((hint) => hint.id === 'light'),
    ...hints.filter((hint) => hint.id === 'background'),
    ...hints.filter((hint) => hint.id === 'ready'),
  ];
  return ordered.slice(0, 4);
};
